import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Field, SectionTitle } from "@/components/ui";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";

interface LinkInfo {
  id: string;
  url: string | null;
  hasPassword: boolean;
  allowComments: boolean;
  allowDownload: boolean;
  showAuthors: boolean;
  welcomeMessage: string | null;
  viewCount: number;
}

/** "Publish board": a read-only link anyone can open, with optional password and welcome message. */
export function PublishSection({ boardId }: { boardId: string }) {
  const t = useT();
  const [link, setLink] = useState<LinkInfo | null | undefined>(undefined);
  const [pw, setPw] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const load = useCallback(
    async () => setLink(await api<LinkInfo | null>(`/api/boards/${boardId}/share-link`)),
    [boardId],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const update = async (patch: Record<string, unknown>) =>
    setLink(await api<LinkInfo>(`/api/boards/${boardId}/share-link`, { method: "PATCH", json: patch }));
  const copy = async () => {
    if (link?.url) {
      await navigator.clipboard.writeText(link.url).catch(() => {});
      setMsg(t.linkCopied);
    }
  };
  if (link === undefined) return null;
  return (
    <div className="mt-6">
      <SectionTitle>{t.publicLink}</SectionTitle>
      {!link ? (
        <Button
          variant="ghost"
          onClick={async () => (
            setLink(await api<LinkInfo>(`/api/boards/${boardId}/share-link`, { method: "POST" })),
            setMsg(null)
          )}
        >
          {t.enableLink}
        </Button>
      ) : (
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={link.url ?? ""}
              className="min-w-0 flex-1 rounded-sm border border-border bg-panel-2 px-2 py-1.5 text-[12px] text-muted outline-none"
            />
            <Button className="px-3 py-1.5 text-xs" onClick={copy}>
              {t.copyLink}
            </Button>
          </div>
          {msg ? <Alert kind="success">{msg}</Alert> : null}
          <div className="text-[11px] text-dim">
            {link.viewCount} {t.views}
          </div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={link.allowComments}
              onChange={(e) => update({ allowComments: e.target.checked })}
            />{" "}
            {t.allowComments}
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={link.allowDownload}
              onChange={(e) => update({ allowDownload: e.target.checked })}
            />{" "}
            {t.allowDownload}
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={link.showAuthors}
              onChange={(e) => update({ showAuthors: e.target.checked })}
            />{" "}
            {t.showAuthors}
          </label>
          <div className="flex flex-wrap items-end gap-2">
            <Field
              label={t.requirePassword}
              name="sharepw"
              type="password"
              placeholder={link.hasPassword ? "••••••••" : ""}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="min-w-40 flex-1"
            />
            <Button
              variant="ghost"
              className="px-3 py-2 text-xs"
              disabled={pw.length < 4}
              onClick={() => (update({ password: pw }), setPw(""))}
            >
              {t.save}
            </Button>
            {link.hasPassword ? (
              <Button
                variant="ghost"
                className="px-3 py-2 text-xs"
                onClick={() => update({ password: null })}
              >
                {t.remove}
              </Button>
            ) : null}
          </div>
          <label className="block">
            <span className="mb-1.5 block text-sm font-semibold">{t.welcomeMessage}</span>
            <textarea
              defaultValue={link.welcomeMessage ?? ""}
              onBlur={(e) => update({ welcomeMessage: e.target.value || null })}
              rows={2}
              className="w-full rounded-sm border border-border bg-panel-2 px-3 py-2 outline-none"
            />
          </label>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              className="px-3 py-1.5 text-xs"
              onClick={async () =>
                setLink(await api<LinkInfo>(`/api/boards/${boardId}/share-link/reset`, { method: "POST" }))
              }
            >
              {t.resetLink}
            </Button>
            <Button
              variant="danger"
              className="px-3 py-1.5 text-xs"
              onClick={async () => (
                await api(`/api/boards/${boardId}/share-link`, { method: "DELETE" }), setLink(null)
              )}
            >
              {t.disableLink}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
